/**
 * @file Context/Auth.jsx
 * @description Authentication context provider. Manages user auth state using Firebase,
 * provides login, signup, logout, Google auth, and token management across the app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../config/firebase';
import { 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut, 
  onAuthStateChanged,
  onIdTokenChanged
} from 'firebase/auth';
import { firebaseLogin as apiFirebaseLogin } from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext();
const REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes
const QUOTA_RETRY_BASE = 5000; // 5 seconds
const MAX_RETRY_ATTEMPTS = 5;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Helper function to get token with exponential backoff
  const getTokenWithRetry = useCallback(async (firebaseUser, forceRefresh = false) => {
    try {
      const token = await firebaseUser.getIdToken(forceRefresh);
      setRetryAttempt(0); // Reset retry attempts on success
      return token;
    } catch (error) {
      if (error.code === 'auth/quota-exceeded' && retryAttempt < MAX_RETRY_ATTEMPTS) {
        const delay = QUOTA_RETRY_BASE * Math.pow(2, retryAttempt);
        console.warn(`Token refresh quota exceeded, retrying in ${delay/1000} seconds`);
        await new Promise(resolve => setTimeout(resolve, delay));
        setRetryAttempt(prev => prev + 1);
        return getTokenWithRetry(firebaseUser, forceRefresh);
      }
      throw error;
    }
  }, [retryAttempt]);

  // Handle token refresh
  useEffect(() => {
    let refreshTimeout;
    let isSubscribed = true;

    const refreshToken = async (firebaseUser) => {
      if (!firebaseUser || !isSubscribed) return;

      try {
        const currentToken = localStorage.getItem('token');
        const tokenExpirationTime = localStorage.getItem('tokenExpirationTime');
        const now = Date.now();

        // Only refresh if we don't have a token or if it's close to expiring
        if (!currentToken || !tokenExpirationTime || now >= parseInt(tokenExpirationTime) - 10 * 60 * 1000) {
          const firebaseToken = await getTokenWithRetry(firebaseUser, false);
          if (!isSubscribed) return;

          // Exchange Firebase token for backend JWT
          try {
            const response = await apiFirebaseLogin({
              email: firebaseUser.email,
              firebaseToken,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL
            });
            const backendToken = response.data.token || firebaseToken;

            localStorage.setItem('token', backendToken);
            localStorage.setItem('tokenExpirationTime', (now + REFRESH_INTERVAL).toString());

            if (user) {
              const updatedUser = { ...user, token: backendToken };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          } catch (refreshErr) {
            console.warn('Token refresh backend call failed:', refreshErr.message);
          }
        }

        // Schedule next refresh
        if (refreshTimeout) clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(() => refreshToken(firebaseUser), REFRESH_INTERVAL - 10 * 60 * 1000);
      } catch (error) {
        console.error('Error in token refresh:', error);
        if (error.code === 'auth/quota-exceeded') {
          // If we hit the quota limit, try again in 1 minute
          refreshTimeout = setTimeout(() => refreshToken(firebaseUser), 60 * 1000);
        }
      }
    };

    const unsubscribeToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await refreshToken(firebaseUser);
      } else {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        // Preserve backend-only sessions (admin login without Firebase)
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!storedUser.token) {
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tokenExpirationTime');
        }
      }
    });

    return () => {
      isSubscribed = false;
      if (refreshTimeout) clearTimeout(refreshTimeout);
      unsubscribeToken();
    };
  }, [user, getTokenWithRetry]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await getTokenWithRetry(firebaseUser, false);
          
          // Verify with backend
          const response = await apiFirebaseLogin({
            email: firebaseUser.email,
            firebaseToken: token,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL
          });

          const userData = response.data.data.user;
          const backendToken = response.data.token || token;
          
          const userState = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || userData.name,
            photoURL: firebaseUser.photoURL,
            token: backendToken,
            isAdmin: userData.isAdmin === true || userData.role === 'admin',
            role: userData.role || 'user'
          };

          setUser(userState);
          localStorage.setItem('token', backendToken);
          localStorage.setItem('user', JSON.stringify(userState));
        } catch (error) {
          console.error('Error verifying with backend:', error);
          if (error.code === 'auth/quota-exceeded') {
            toast.error('Authentication service is busy. Please try again in a few minutes.');
          } else {
            toast.error('Authentication failed. Please try again.');
          }
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          await signOut(auth);
        }
      } else {
        // Preserve backend-only sessions (admin login without Firebase)
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser.token) {
          setUser(storedUser);
        } else {
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [getTokenWithRetry]);

  // Sign in with email and password
  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await getTokenWithRetry(result.user, true);
      
      // Verify with backend
      const response = await apiFirebaseLogin({
        email: result.user.email,
        firebaseToken: token,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });

      const userData = response.data.data.user;
      const backendToken = response.data.token || token;
      
      const userState = {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName || userData.name,
        photoURL: result.user.photoURL,
        token: backendToken,
        isAdmin: userData.isAdmin === true,
        role: userData.role || 'user'
      };

      setUser(userState);
      localStorage.setItem('token', backendToken);
      localStorage.setItem('user', JSON.stringify(userState));

      return userState;
    } catch (error) {
      console.error('Error signing in with email:', error);
      if (error.code === 'auth/quota-exceeded') {
        toast.error('Authentication service is busy. Please try again in a few minutes.');
      } else {
        toast.error('Login failed. Please check your credentials and try again.');
      }
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const token = await getTokenWithRetry(result.user, true);
      
      // Verify with backend immediately
      const response = await apiFirebaseLogin({
        email: result.user.email,
        firebaseToken: token,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });

      const userData = response.data.data.user;
      const backendToken = response.data.token || token;
      
      const userState = {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName || userData.name,
        photoURL: result.user.photoURL,
        token: backendToken,
        isAdmin: userData.isAdmin === true,
        role: userData.role || 'user'
      };

      setUser(userState);
      localStorage.setItem('token', backendToken);
      localStorage.setItem('user', JSON.stringify(userState));

      return userState;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const token = await getTokenWithRetry(result.user, true);
      return {
        ...result.user,
        token
      };
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      await signOut(auth);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpirationTime');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signInWithEmail,
    signInWithGoogle,
    signUpWithEmail,
    resetPassword,
    signOut: signOutUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 